/**
 * Primitives shared by every chart in ChartsComponent.
 *
 * Holds the validated categorical palettes, colour and number formatting, and
 * the small theme/motion/size hooks the charts read. Anything specific to one
 * chart's data model (part-to-whole slices, cartesian series) belongs in that
 * chart's own folder, so this module stays chart-agnostic.
 */

import { useCallback, useRef, useState, useSyncExternalStore } from "react";

/* ------------------------------------------------------------------ */
/* Palettes                                                            */
/* ------------------------------------------------------------------ */

/**
 * Categorical hues, assigned in fixed order and never cycled.
 *
 * Both orders are validated for colorblind separation (worst adjacent pair
 * deltaE 8.1 deutan light / 9.6 dark), chroma, and lightness against the app
 * card surfaces (#ffffff light, #131320 dark). Light-mode fills sit below 3:1
 * against white, which is why the legend always carries a text label and a data
 * table view is available: identity is never colour-alone.
 *
 * Do not reorder these arrays. Adjacent-pair separation is a property of the
 * order, so a shuffle silently breaks the guarantee.
 */
export const CHART_PALETTE_LIGHT = [
  "#6366f1", // indigo
  "#f59e0b", // amber
  "#0ea5e9", // sky
  "#ef4444", // red
  "#10b981", // emerald
  "#a855f7", // purple
  "#f97316", // orange
  "#14b8a6", // teal
] as const;

/** Dark-mode steps of the same hues, re-stepped for the dark card surface. */
export const CHART_PALETTE_DARK = [
  "#6366f1",
  "#d97706",
  "#0284c7",
  "#ef4444",
  "#059669",
  "#a855f7",
  "#ea580c",
  "#0d9488",
] as const;

/**
 * Categorical hues for all-pairs forms: scatter, bubble, and anything else
 * where any two marks can end up side by side.
 *
 * The eight-slot orders above are validated on adjacent pairs only, which is
 * the right test when assignment never skips (stacked segments, neighbouring
 * bars, lines read against a legend). Scattered marks have no such order, so
 * every pair must clear the floors, and no ordering of the full eight can do
 * that. These are the three slots that pass all-pairs in both modes: worst pair
 * deltaE 16.3 CVD / 27.4 normal in light, 13.8 / 23.3 in dark.
 *
 * A fourth group is not a palette problem to solve. Fold the tail into a
 * neutral "Other" or facet the chart instead.
 */
export const CHART_PALETTE_ALLPAIRS_LIGHT = ["#6366f1", "#f97316", "#14b8a6"] as const;
export const CHART_PALETTE_ALLPAIRS_DARK = ["#6366f1", "#ea580c", "#0d9488"] as const;

/** Neutral used for the folded "Other" bucket so it never reads as an entity. */
export const OTHER_COLOR_LIGHT = "#94a3b8";
export const OTHER_COLOR_DARK = "#64748b";

/** Surface colours the 2px slice gap and marker rings are drawn in. */
export const SURFACE_LIGHT = "#ffffff";
export const SURFACE_DARK = "#131320";

/* ------------------------------------------------------------------ */
/* Cartesian mark treatment                                            */
/* ------------------------------------------------------------------ */

/**
 * Outline treatment shared by every cartesian mark: a translucent fill in the
 * series hue, closed by a solid stroke of the same colour.
 *
 * The stroke does the work the fill cannot at this opacity — it gives the mark
 * a definite edge, so a short bar is still a bar and two overlapping areas
 * stay tellable apart instead of muddying into a third colour. Keep the two
 * together: a fill this light with no stroke reads as a stain, and a stroke
 * with a solid fill is just a bordered block.
 */
export const MARK_FILL_OPACITY = 0.18;
export const MARK_STROKE_WIDTH = 1.5;

/**
 * A bar is outline-ONLY: the fill is empty and the stroke carries the hue.
 *
 * An area keeps `MARK_FILL_OPACITY` instead — an area with no fill is just a
 * line, so the two marks differ here on purpose. A bar has a closed shape to
 * outline; an area does not.
 */
export const BAR_FILL = "transparent";

/** Rounded at the data end, square at the baseline the bar grows from. */
export const MARK_CORNER_RADIUS = 6;

/**
 * Separation between adjacent marks: donut segments, gauge arcs, treemap tiles.
 *
 * Stated in PIXELS because that is what the eye compares across cards. Note
 * that a ring chart cannot consume it directly — its gap comes from
 * `paddingAngle`, an ANGLE, so the pixel result scales with radius. The same
 * 2 degrees reads as ~5px on the thick distribution gauge (mid-radius ~143px)
 * but only ~3.6px on a standard donut (~102px), which is why those two looked
 * unequal despite sharing a number. Rings convert via SEGMENT_PADDING_ANGLE
 * below; tiles use this value as-is.
 */
export const MARK_GAP_PX = 5;

/**
 * Padding between donut segments, in degrees, chosen so the gap lands near
 * MARK_GAP_PX at the standard donut's radius. Tuned per-chart rather than
 * shared with the gauge: matching the NUMBER would leave the gaps unequal, and
 * matching the gap is the point.
 */
export const SEGMENT_PADDING_ANGLE = 3;

/**
 * Glow in the mark's own hue. A hollow bar reads as a thin wire without it;
 * the glow gives the stroke enough presence to hold its own shape.
 *
 * Two layers, because that is what makes it read as neon rather than as a
 * blur: a tight bright core hugging the stroke, and a wide soft halo bleeding
 * past it. One layer alone gives either a hard edge or a smudge.
 */
/**
 * The two radii behind {@link markGlow}, exported because the neon is not
 * only a CSS filter: the PAG Network Graph draws to a canvas, where the same
 * two layers become a baked SVG core and a per-node canvas shadow. One pair
 * of numbers, so an SVG chart and the canvas graph cannot drift apart.
 */
export const GLOW_CORE_BLUR = 1.5;
export const GLOW_HALO_BLUR = 7;

export const markGlow = (color: string): string =>
  `drop-shadow(0 0 ${GLOW_CORE_BLUR}px ${color}) drop-shadow(0 0 ${GLOW_HALO_BLUR}px ${color})`;

/**
 * Glow for a FILLED mark -- an area band rather than an outline.
 *
 * Much lighter than {@link markGlow}, and not a matter of taste: a drop-shadow
 * follows the painted silhouette, so on a thin stroke it reads as a lit edge
 * but on a filled shape it throws a halo the size of the shape. Stacked or
 * overlapping series then compound it, and the plot hazes over while the
 * outlined charts beside it stay crisp. One tight layer keeps the edge lit
 * without the bloom.
 */
export const areaGlow = (color: string): string => `drop-shadow(0 0 2px ${color})`;

/**
 * DOM counterpart of {@link markGlow}. An SVG `filter` follows the painted
 * silhouette, which is right for a mark; on an HTML box it would also blur
 * whatever the box contains, so border-hugging `box-shadow`s are used instead.
 *
 * Same two-layer build as `markGlow` — tight core, wide halo — plus an inner
 * shadow, which is the part that makes an unfilled box read as lit from its
 * own edge rather than as a plain outline.
 *
 * Expects a 6-digit hex, since it appends an alpha pair.
 */
export const markGlowShadow = (color: string): string =>
  `0 0 3px ${color}80, 0 0 12px ${color}4d, inset 0 0 10px ${color}1f`;

/*
 * There is deliberately no text-glow helper here. The glow belongs on the
 * marks; on a numeral it smears the counters and costs more legibility than
 * the neon buys, which shows up worst on a light background. Figures carry
 * their hue and nothing else.
 */

/** Gridlines are dashed, so they read as reference rather than as data. */
export const GRID_DASH = "3 3";

/** Ticks carry weight to stay legible against the dashed grid behind them. */
export const AXIS_TICK_FONT_SIZE = 12;
export const AXIS_TICK_FONT_WEIGHT = 600;

/* ------------------------------------------------------------------ */
/* Colour validation                                                   */
/* ------------------------------------------------------------------ */

const HEX_RE = /^#(?:[0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;
const CSS_FN_RE = /^(?:rgb|rgba|hsl|hsla|oklch|oklab|lab|lch|color|var)\(/i;
const NAMED_RE = /^[a-zA-Z]+$/;

/**
 * Loose guard against a caller passing a broken colour (empty string, number,
 * object). Anything that fails falls back to the palette slot rather than
 * rendering an invisible or black slice.
 */
export function isValidColor(value: unknown): value is string {
  if (typeof value !== "string") return false;
  const trimmed = value.trim();
  if (!trimmed) return false;
  return HEX_RE.test(trimmed) || CSS_FN_RE.test(trimmed) || NAMED_RE.test(trimmed);
}

/* ------------------------------------------------------------------ */
/* Formatting                                                          */
/* ------------------------------------------------------------------ */

/** Locale number with a sane fraction cap; never throws on junk input. */
export function formatNumber(value: unknown, unit = ""): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return `0${unit}`;
  return `${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}${unit}`;
}

/** Compact form for the donut centre, where space is tight (1.2K, 3.4M). */
export function formatCompact(value: unknown, unit = ""): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return `0${unit}`;
  const abs = Math.abs(n);
  if (abs >= 1_000_000_000) return `${trimZero(n / 1_000_000_000)}B${unit}`;
  if (abs >= 1_000_000) return `${trimZero(n / 1_000_000)}M${unit}`;
  if (abs >= 10_000) return `${trimZero(n / 1_000)}K${unit}`;
  return formatNumber(n, unit);
}

function trimZero(n: number): string {
  return n.toFixed(1).replace(/\.0$/, "");
}

/** Share as a rounded percent string. Guards the zero-total divide. */
export function formatShare(value: number, total: number): string {
  if (!Number.isFinite(value) || !Number.isFinite(total) || total <= 0) return "0%";
  const pct = (value / total) * 100;
  // Keep one decimal for slivers so a real slice never displays as "0%".
  return pct > 0 && pct < 1 ? `${pct.toFixed(1)}%` : `${Math.round(pct)}%`;
}

/**
 * Normalizes whatever a caller passed as `error` into a displayable string.
 * Accepts a string, an Error, or null, and never yields an empty message.
 */
export function resolveErrorMessage(
  error: string | Error | null | undefined,
): string | null {
  if (!error) return null;
  if (typeof error === "string") return error.trim() || "An unexpected error occurred.";
  return error?.message?.trim() || "An unexpected error occurred.";
}

/**
 * Clamps a caller-supplied plot height, guarding 0, negatives, and non-numbers.
 * Charts need a definite height because their container fills its parent.
 */
export function resolvePlotHeight(height: unknown, fallback: number): number {
  const n = Number(height);
  return Number.isFinite(n) && n > 0 ? Math.max(140, n) : fallback;
}

/* ------------------------------------------------------------------ */
/* Environment hooks                                                   */
/* ------------------------------------------------------------------ */

function subscribeToTheme(onChange: () => void): () => void {
  if (typeof document === "undefined") return () => {};
  const observer = new MutationObserver(onChange);
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["class"],
  });
  return () => observer.disconnect();
}

function getThemeSnapshot(): boolean {
  if (typeof document === "undefined") return false;
  return document.documentElement.classList.contains("dark");
}

/**
 * Tracks the app's `.dark` root class so chart fills can switch to the
 * dark-validated steps. Uses useSyncExternalStore rather than an effect, so no
 * extra render pass and no effect to clean up.
 */
export function useIsDarkTheme(): boolean {
  return useSyncExternalStore(subscribeToTheme, getThemeSnapshot, () => false);
}

function subscribeToMotion(onChange: () => void): () => void {
  if (typeof window === "undefined" || !window.matchMedia) return () => {};
  const query = window.matchMedia("(prefers-reduced-motion: reduce)");
  // Safari below 14 only supports the deprecated listener API.
  if (query.addEventListener) {
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }
  query.addListener(onChange);
  return () => query.removeListener(onChange);
}

function getMotionSnapshot(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/** True when the viewer asked the OS to reduce motion. */
export function usePrefersReducedMotion(): boolean {
  return useSyncExternalStore(subscribeToMotion, getMotionSnapshot, () => false);
}

/**
 * Measures the element a chart is rendered into, so layout can respond to the
 * card it was dropped in rather than to the viewport. A chart in a narrow
 * dashboard column needs to stack even on a wide screen, which a media query
 * cannot see.
 *
 * CSS container queries would be the obvious tool, but `container-type:
 * inline-size` stops an element contributing its content width to the parent,
 * so the chart collapses inside any shrink-to-fit ancestor (an inline-flex
 * wrapper, a `w-fit` card, an auto-width grid track). Measuring sidesteps that
 * entirely and works under every parent layout.
 *
 * Returns a ref callback and the current width. Width is 0 until first measure,
 * which reads as the narrow, stacked layout - mobile first.
 */
export function useElementWidth<T extends HTMLElement>(): [
  (node: T | null) => void,
  number,
] {
  const [width, setWidth] = useState(0);
  const observerRef = useRef<ResizeObserver | null>(null);

  const measuredRef = useCallback((node: T | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;

    if (!node) return;
    if (typeof ResizeObserver === "undefined") {
      // Older browsers and non-DOM test environments: measure once and accept
      // that the layout will not follow later resizes.
      setWidth(node.getBoundingClientRect?.().width ?? 0);
      return;
    }

    setWidth(node.getBoundingClientRect().width);
    const observer = new ResizeObserver((entries) => {
      const next = entries?.[0]?.contentRect?.width;
      if (typeof next === "number" && Number.isFinite(next)) setWidth(next);
    });
    observer.observe(node);
    observerRef.current = observer;
  }, []);

  return [measuredRef, width];
}
